import React, { useContext, useState, useEffect } from "react";
import { UserContext, useStepperContext } from "../../context/MainContext";
import { TextareaField, SelectField } from "../InputField";
import { userLenderSearch } from "../../actions/userLenderSearch";
import { titleCompanySearch } from "../../actions/titleCompanySearch";
import { userRealtorSearch } from "../../actions/userRealtorSearch";
import { cloudinaryUrl } from "../../constants/CloudinaryConstants";

const Remarks = () => {
  const { propertyData, setPropertyData } = useStepperContext();
  const { user } = useContext(UserContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPropertyData({ ...propertyData, [name]: value });
  };

  // Lenders
  const [results, setResults] = useState(null);
  const [lenderQuery, setLenderQuery] = useState("");
  const [lenderSearchAttempted, setLenderSearchAttempted] = useState(false);
  const [lenderSaved, setLenderSaved] = useState(false);

  const handleChoose = (userId) => {
    setPropertyData({ ...propertyData, lender_used: userId });
  };

  // Title companies
  const [titleResults, setTitleResults] = useState(null);
  const [titleQuery, setTitleQuery] = useState("");
  const [titleSearchAttempted, setTitleSearchAttempted] = useState(false);
  const [titleSaved, setTitleSaved] = useState(false);

  const handleTitleChoose = (userId) => {
    setPropertyData({ ...propertyData, title_company_used: userId });
  };

  // Load initial lists (empty search)
  useEffect(() => {
    userLenderSearch("", "", { setResults });
    titleCompanySearch("", { setTitleResults });
  }, []);

  // Debounced auto-search when typing in lenderQuery
  useEffect(() => {
    if (lenderQuery === "") return; // skip empty
    const t = setTimeout(() => {
      setLenderSearchAttempted(true);
      userLenderSearch(lenderQuery.trim(), "", { setResults });
    }, 400);
    return () => clearTimeout(t);
  }, [lenderQuery]);

  // Debounced auto-search when typing in titleQuery
  useEffect(() => {
    if (titleQuery === "") return; // skip empty
    const t = setTimeout(() => {
      setTitleSearchAttempted(true);
      titleCompanySearch(titleQuery.trim(), { setTitleResults });
    }, 400);
    return () => clearTimeout(t);
  }, [titleQuery]);

  // Realtors (builder flows)
  const [preferredRealtor, setPreferredRealtor] = useState(null);
  const [realtors, setRealtors] = useState(null);
  const [realtorActiveIndex, setRealtorActiveIndex] = useState(null);
  const handleRealtorSearch = () => {
    if (preferredRealtor) userRealtorSearch(preferredRealtor, { setRealtors });
  };
  const handleRealtorChoose = (result) => {
    setPropertyData({
      ...propertyData,
      realtor_id: result.user_id,
      realtor_name: `${result.legal_first_name} ${result.legal_last_name}`,
      realtor_picture: result.picture_url,
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-bold text-30">Remarks</h1>

      {/* Lender & Title Company Card (combined) - Only for Sale properties */}
      {propertyData["for"] === "Sale" && (
        <div className="bg-white border rounded-lg shadow p-4 mb-4">
          <p className="text-slate-600 text-sm text-center mb-4">
            Optional: You can add or edit this information later in the property
            edit view
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold mb-2">
                Link a preferred lending company / lender
              </h3>
              <p className="text-xs text-gray-600 mb-2">
                Choose from dropdown or enter in the field below.
              </p>
              <select
                className="bg-white border border-slate-300 px-3 py-2 rounded w-full mb-2"
                value={propertyData["lender_used"] || ""}
                onChange={(e) => handleChoose(e.target.value)}
              >
                <option value="">
                  Choose lender to be linked to this property listing
                </option>
                {results &&
                  results.map((r) => (
                    <option key={r.user_id} value={r.user_id}>
                      {r.brokerage_name || "N/A"} - {r.legal_last_name},{" "}
                      {r.legal_first_name}
                    </option>
                  ))}
              </select>
              <input
                className="bg-white border border-slate-300 px-3 py-2 rounded w-full"
                type="text"
                placeholder="Or enter lender company or contact name"
                value={propertyData["lender_text"] || ""}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    lender_text: e.target.value,
                  })
                }
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1 bg-blue-600 text-white rounded"
                  onClick={() => {
                    setPropertyData((prev) => ({
                      ...prev,
                      lender_text: prev.lender_text || "",
                      lender_used: "",
                    }));
                    setLenderSaved(true);
                    setTimeout(() => setLenderSaved(false), 2500);
                  }}
                >
                  Save
                </button>
                {lenderSaved && (
                  <span className="text-sm text-green-600">Saved</span>
                )}
              </div>
              {propertyData["lender_used"] && (
                <p className="text-sm font-bold mt-2 text-green-700">
                  ✓ Lender selected
                </p>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2">
                Link a preferred title company / closer
              </h3>
              <p className="text-xs text-gray-600 mb-2">
                Choose from dropdown or enter in the field below.
              </p>
              <select
                className="bg-white border border-slate-300 px-3 py-2 rounded w-full mb-2"
                value={propertyData["title_company_used"] || ""}
                onChange={(e) => handleTitleChoose(e.target.value)}
              >
                <option value="">
                  Choose title company to be linked to this property listing
                </option>
                {titleResults &&
                  titleResults.map((t) => (
                    <option key={t.user_id} value={t.user_id}>
                      {t.brokerage_name || "N/A"} - {t.legal_last_name},{" "}
                      {t.legal_first_name}
                    </option>
                  ))}
              </select>
              <input
                className="bg-white border border-slate-300 px-3 py-2 rounded w-full"
                type="text"
                placeholder="Or enter title company or closer name"
                value={propertyData["title_company_text"] || ""}
                onChange={(e) =>
                  setPropertyData({
                    ...propertyData,
                    title_company_text: e.target.value,
                  })
                }
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1 bg-blue-600 text-white rounded"
                  onClick={() => {
                    setPropertyData((prev) => ({
                      ...prev,
                      title_company_text: prev.title_company_text || "",
                      title_company_used: "",
                    }));
                    setTitleSaved(true);
                    setTimeout(() => setTitleSaved(false), 2500);
                  }}
                >
                  Save
                </button>
                {titleSaved && (
                  <span className="text-sm text-green-600">Saved</span>
                )}
              </div>
              {propertyData["title_company_used"] && (
                <p className="text-sm font-bold mt-2 text-green-700">
                  ✓ Title company selected
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remarks text box */}
      <div className="bg-white border rounded-lg shadow p-4 mb-4">
        <h3 className="font-semibold mb-2">
          Listing Notes (Visible to All Users)
        </h3>
        <TextareaField
          name="agentremarks"
          placeholder="Any common remarks about the property"
          className="bg-slate-100 w-full h-40 p-3"
          value={propertyData["agentremarks"] || ""}
          onChange={(e) =>
            setPropertyData({ ...propertyData, agentremarks: e.target.value })
          }
        />
      </div>

      {/* Private Agent Remarks - Only visible to Real Estate Professionals */}
      {(user?.class_type === "Realtor" ||
        user?.class_type === "Builder" ||
        user?.class_type === "Title Company" ||
        user?.class_type === "Lender") && (
        <div className="bg-white border rounded-lg shadow p-4 mb-4 border-blue-300">
          <h3 className="font-semibold mb-2 text-blue-700">
            Private Agent Notes (Visible Only to Professionals)
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            These notes are for real estate professionals only and will not be
            visible to consumers.
          </p>
          <TextareaField
            name="agent_private_remarks"
            placeholder="Private notes for agents, brokers, and industry professionals"
            className="bg-blue-50 w-full h-40 p-3 border border-blue-200"
            value={propertyData["agent_private_remarks"] || ""}
            onChange={(e) =>
              setPropertyData({
                ...propertyData,
                agent_private_remarks: e.target.value,
              })
            }
          />
        </div>
      )}

      {/* Listing Dates Card */}
      <div className="bg-white border rounded-lg shadow p-4 mb-4">
        <h1 className="font-bold text-20">Listing Dates</h1>
        <div className="flex flex-col gap-4 mt-3">
          <div className="flex flex-col">
            <label className="font-bold">
              Listing Start Date <span className="text-red-600 ml-1">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">
              When did this listing become active?
            </p>
            <input
              className="bg-slate-300 px-4 py-2 w-full"
              type="date"
              name="listing_start_date"
              value={propertyData["listing_start_date"] || ""}
              onChange={handleChange}
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="font-bold">
              Listing Expiration Date{" "}
              <span className="text-red-600 ml-1">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">
              When does this listing expire?
            </p>
            <input
              className="bg-slate-300 px-4 py-2 w-full"
              type="date"
              name="listing_expiration_date"
              value={propertyData["listing_expiration_date"] || ""}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      {/* Builder-only: preferred realtor */}
      {user?.class_type === "Builder" && (
        <div className="bg-white border rounded-lg shadow p-4 mb-4">
          <h1 className="pb-2">
            <b>Link a preferred realtor</b> - Use Realtors First and Last Name.
          </h1>
          <div className="flex gap-4 items-center mb-4">
            <input
              className="bg-slate-300 px-4 py-2 flex-1"
              type="text"
              onChange={(e) => setPreferredRealtor(e.target.value)}
            />
            <button
              className="bg-black text-white rounded px-4 py-2"
              onClick={() => handleRealtorSearch()}
            >
              Search
            </button>
          </div>

          <div className="w-full">
            {realtors &&
              realtors.map((result, index) => (
                <button
                  key={index}
                  className={
                    realtorActiveIndex === index &&
                    result.user_id === propertyData["realtor_id"]
                      ? "border-2 py-2 px-2 w-[250px] bg-black text-white mb-2"
                      : "border-2 py-2 px-2 w-[250px] hover:bg-black hover:text-white mb-2"
                  }
                  onClick={() => {
                    handleRealtorChoose(result);
                    setRealtorActiveIndex(index);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <img
                      className="h-[50px] w-[50px] object-cover rounded-full"
                      src={
                        result?.picture_url
                          ? cloudinaryUrl(result?.picture_url)
                          : "/error.png"
                      }
                      alt="realtor"
                    />
                    <div>
                      <div className="flex gap-1">
                        <h1 className="text-19">{result.legal_first_name}</h1>
                        <h1 className="text-19">{result.legal_last_name}</h1>
                      </div>
                      <p className="text-blue-500">{result.class_type}</p>
                      <p>{result.position}</p>
                    </div>
                  </div>
                </button>
              ))}

            {propertyData["realtor_id"] && (
              <div className="mt-3">
                <p className="font-bold">Realtor chosen for this property</p>
                <div className="border-2 py-2 px-2 w-[250px] bg-black text-white mt-2 flex items-center gap-3">
                  <img
                    className="h-[50px] w-[50px] object-cover rounded-full"
                    src={
                      propertyData["realtor_picture"]
                        ? cloudinaryUrl(propertyData["realtor_picture"])
                        : "/error.png"
                    }
                    alt="realtor"
                  />
                  <div>
                    <h1 className="text-19">{propertyData["realtor_name"]}</h1>
                    <p>Realtor</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Remarks;
